/**
 * Device identity helper (renderer side)
 *
 * Wraps the preload bridge `window.bodapro.getDeviceIdentity()` and caches the
 * result at module level so repeated subscription calls don't re-cross the IPC
 * boundary. Desktop (Electron) only — on web there is no bridge and this
 * resolves to null, in which case callers simply omit the device id.
 */

let cachedIdentity = null;
let inflight = null;

/**
 * Get the current device identity, or null when unavailable (e.g. web build).
 * @returns {Promise<{device_id:string, source:string, name:string, platform:string, app_version:string}|null>}
 */
export async function getDeviceIdentity() {
    if (cachedIdentity) {
        return cachedIdentity;
    }
    if (inflight) {
        return inflight;
    }

    const bridge = typeof window !== 'undefined' ? window.bodapro : null;
    if (!bridge || typeof bridge.getDeviceIdentity !== 'function') {
        return null;
    }

    inflight = (async () => {
        try {
            const identity = await bridge.getDeviceIdentity();
            cachedIdentity = identity || null;
            return cachedIdentity;
        } catch (error) {
            console.warn('Failed to resolve device identity:', error);
            return null;
        } finally {
            inflight = null;
        }
    })();

    return inflight;
}

/**
 * Convenience accessor for just the device id string (or null).
 */
export async function getDeviceId() {
    const identity = await getDeviceIdentity();
    return identity ? identity.device_id : null;
}
